import type { ICalculateAdjustmentsRequest, IProductPriceBasis, IQualityInput, SpecType } from '@local/lib';

import {
  QualityFormulaType as ApiQualityFormulaType,
  ProductPriceBasisType,
  QualityFormulaType,
  TierMode,
} from '@local/lib';
import { ZodError } from 'zod';

import type {
  ProductPriceBasisState,
  RangeFormulaRow,
  SingleValueFormulaRow,
  SpecState,
  ThresholdFormulaRow,
} from './qualityFormulaTypes';

import { createDefaultPriceBasis } from './qualityFormulaDefaults';
import { isMultiRowFormulaType, resolveTestPriceComponentsTotal, SPEC_TYPES } from './qualityFormulaMetadata';

/**
 * Interprets a rejection from calculateQualityAdjustments's pre-flight parse — the same
 * CalculateAdjustmentsRequestSchema the API enforces server-side (see pricingEngineApi.ts) — into
 * one "SpecLabel: message" line per violated business rule (zero contracted spec, contracted spec
 * of exactly 100, etc.). Those rules live solely in the shared Zod schema, not duplicated here, so
 * this only needs to label *which spec* an issue belongs to. Returns null for any other error type
 * so callers can fall back to a generic error notification.
 */
export const describeCalculateAdjustmentsValidationError = (error: unknown): null | string[] => {
  if (!(error instanceof ZodError)) return null;

  return error.issues.map(issue => {
    // Most issues this schema can raise are scoped to one qualityInputs entry (spec type as the
    // second path segment: ['qualityInputs', specType, ...]) — a few, like the quantity-spec
    // mutual-exclusivity rule, are scoped to the whole qualityInputs map instead, in which case
    // there's no single spec to label and the raw message is shown as-is.
    const specLabel = SPEC_TYPES.find(spec => spec.key === issue.path[1])?.label;
    return specLabel ? `${specLabel}: ${issue.message}` : issue.message;
  });
};

export interface BuildCalculateAdjustmentsRequestInput {
  actualValues: Record<SpecType, number>;
  deliveredQuantity: number;
  specStates: Record<SpecType, SpecState>;
  /** The playground's own editable Price Component values — see `resolveTestSpecState` below. */
  testComponentValues: Record<string, number>;
  /** The playground's own editable Deemed Price overrides, keyed by spec type — see
   * `resolveTestSpecState` below. */
  testDeemedPriceOverrides: Partial<Record<SpecType, number>>;
  tradePrice: number;
}

/**
 * Converts Step 1's Product Price Basis form state into the API's `IProductPriceBasis` shape —
 * `Contract Price` collapses to the bare `{ type }` the schema expects (no further fields), while
 * `Price Components`/`Deemed Price` carry their one relevant field each. Only ever called for a
 * PERCENTAGE spec (see `buildQualityInputForSpec`'s Percentage branch below) — every other
 * formula type has no Product Price Basis to send.
 */
const toApiProductPriceBasis = (priceBasis: ProductPriceBasisState): IProductPriceBasis => {
  if (priceBasis.type === ProductPriceBasisType.PriceComponents) {
    return { componentKeys: priceBasis.selectedComponentKeys, type: ProductPriceBasisType.PriceComponents };
  }
  if (priceBasis.type === ProductPriceBasisType.DeemedPrice) {
    return { deemedPrice: priceBasis.deemedPrice, type: ProductPriceBasisType.DeemedPrice };
  }
  return { type: ProductPriceBasisType.ContractPrice };
};

/**
 * Inverse of `toApiProductPriceBasis` — rebuilds Step 1's form state from a persisted template's
 * `productPriceBasis`. Also covers its absence (a template saved before this feature existed),
 * defaulting to Contract Price the same way `createDefaultSpecState` does for a brand-new spec.
 * Exported so `fromTemplateConfig` (templateConfigConversion.ts) can reuse this mapping instead
 * of duplicating it.
 */
export const fromApiProductPriceBasis = (productPriceBasis?: IProductPriceBasis): ProductPriceBasisState => {
  if (productPriceBasis?.type === ProductPriceBasisType.PriceComponents) {
    return {
      deemedPrice: 0,
      selectedComponentKeys: productPriceBasis.componentKeys,
      type: ProductPriceBasisType.PriceComponents,
    };
  }
  if (productPriceBasis?.type === ProductPriceBasisType.DeemedPrice) {
    return {
      deemedPrice: productPriceBasis.deemedPrice,
      selectedComponentKeys: [],
      type: ProductPriceBasisType.DeemedPrice,
    };
  }
  return createDefaultPriceBasis();
};

/**
 * Builds one spec's `qualityInputs` entry — factored out of `buildCalculateAdjustmentsRequest` so
 * that function can assemble the full map via `reduce` (no `qualityInputs[spec.key] = ...`
 * mutation) instead of needing this branching inlined into a `forEach`. Exported so
 * `toTemplateConfig` (templateConfigConversion.ts) can reuse this same per-formula-type
 * regrouping instead of duplicating it — a template's persisted config is this same shape minus
 * `actualSpec`, plus `specType`. Takes `Omit<SpecState, 'collapsed' | 'enabled'>` rather than the
 * full `SpecState` since neither UI-only flag is ever read here, which is also exactly the shape
 * the Template Editor's own form state (`TemplateSpecConfig`) already is.
 */
export const buildQualityInputForSpec = (
  actualSpec: number,
  state: Omit<SpecState, 'collapsed' | 'enabled'>,
): IQualityInput => {
  const { contractedSpec, formulaType, precision, rows } = state;

  if (isMultiRowFormulaType(formulaType)) {
    if (formulaType === QualityFormulaType.Percentage) {
      const productPriceBasis = toApiProductPriceBasis(state.priceBasis);
      return state.tierMode === TierMode.Threshold
        ? {
            actualSpec,
            contractedSpec,
            formulaRows: (rows as ThresholdFormulaRow[]).map(row => ({
              adjustmentMode: row.adjustmentMode,
              isFlatAdjustment: row.isFlatAdjustment,
              percentFactor: row.percentFactor,
              perIncrement: row.perIncrement,
            })),
            formulaType: ApiQualityFormulaType.Percentage,
            precision,
            productPriceBasis,
            tierMode: TierMode.Threshold,
          }
        : {
            actualSpec,
            contractedSpec,
            formulaRows: (rows as RangeFormulaRow[]).map(row => ({
              adjustmentMode: row.adjustmentMode,
              isFlatAdjustment: row.isFlatAdjustment,
              lowerBound: row.lowerBound,
              lowerBoundOperator: row.lowerBoundOperator,
              percentFactor: row.percentFactor,
              perIncrement: row.perIncrement,
              upperBound: row.upperBound,
              upperBoundOperator: row.upperBoundOperator,
            })),
            formulaType: ApiQualityFormulaType.Percentage,
            precision,
            productPriceBasis,
            tierMode: TierMode.Range,
          };
    }

    return state.tierMode === TierMode.Threshold
      ? {
          actualSpec,
          contractedSpec,
          formulaRows: (rows as ThresholdFormulaRow[]).map(row => ({
            adjustmentMode: row.adjustmentMode,
            isFlatAdjustment: row.isFlatAdjustment,
            perIncrement: row.perIncrement,
            qualityPricePerUnit: row.qualityPricePerUnit,
          })),
          formulaType: ApiQualityFormulaType.PerUnit,
          precision,
          tierMode: TierMode.Threshold,
        }
      : {
          actualSpec,
          contractedSpec,
          formulaRows: (rows as RangeFormulaRow[]).map(row => ({
            adjustmentMode: row.adjustmentMode,
            isFlatAdjustment: row.isFlatAdjustment,
            lowerBound: row.lowerBound,
            lowerBoundOperator: row.lowerBoundOperator,
            perIncrement: row.perIncrement,
            qualityPricePerUnit: row.qualityPricePerUnit,
            upperBound: row.upperBound,
            upperBoundOperator: row.upperBoundOperator,
          })),
          formulaType: ApiQualityFormulaType.PerUnit,
          precision,
          tierMode: TierMode.Range,
        };
  }

  if (formulaType === QualityFormulaType.AdjustedSalesPrice) {
    return {
      actualSpec,
      formulaRows: [{ contractedSpec: (rows[0] as SingleValueFormulaRow).contractedSpec }],
      formulaType: ApiQualityFormulaType.AdjustedSalesPrice,
      precision,
    };
  }

  if (formulaType === QualityFormulaType.QuantityAdjustedToContracted) {
    return {
      actualSpec,
      formulaRows: [{ lowerBound: (rows[0] as SingleValueFormulaRow).lowerBound }],
      formulaType: ApiQualityFormulaType.QuantityAdjustedToContracted,
      precision,
    };
  }

  if (formulaType === QualityFormulaType.QuantityAdjustedByPercent) {
    return {
      actualSpec,
      formulaType: ApiQualityFormulaType.QuantityAdjustedByPercent,
      precision,
    };
  }

  return {
    actualSpec,
    formulaRows: [{ lowerBound: (rows[0] as SingleValueFormulaRow).lowerBound }],
    formulaType: ApiQualityFormulaType.QuantityAdjustedToDry,
    precision,
  };
};

/**
 * Swaps a Price-Components spec's basis for an equivalent Deemed Price override computed from the
 * playground's own editable component values (see FormulaTestingPanel) — the API always resolves
 * `PriceComponents` against its own fixed catalog (`resolveEffectiveTradePrice`), so this is the
 * only way the playground's component inputs can actually affect the preview without the API
 * needing to accept ad-hoc component values. Also substitutes a Deemed-Price spec's own
 * `deemedPrice` with its playground override, if one was entered — testing a formula must never
 * change what's actually configured for a spec (see `testDeemedPriceOverrides`' own doc comment),
 * only read it as the override's starting point. Only ever used for the preview request below; a
 * saved template still keeps its real basis untouched, since `toTemplateConfig`
 * (templateConfigConversion.ts) calls `buildQualityInputForSpec` directly instead of going through
 * this function.
 */
const resolveTestSpecState = (
  state: Omit<SpecState, 'collapsed' | 'enabled'>,
  testComponentValues: Record<string, number>,
  testDeemedPriceOverride: number | undefined,
): Omit<SpecState, 'collapsed' | 'enabled'> => {
  if (state.formulaType !== QualityFormulaType.Percentage) return state;

  if (state.priceBasis.type === ProductPriceBasisType.PriceComponents) {
    return {
      ...state,
      priceBasis: {
        ...state.priceBasis,
        deemedPrice: resolveTestPriceComponentsTotal(state.priceBasis.selectedComponentKeys, testComponentValues),
        type: ProductPriceBasisType.DeemedPrice,
      },
    };
  }

  if (state.priceBasis.type === ProductPriceBasisType.DeemedPrice && testDeemedPriceOverride !== undefined) {
    return { ...state, priceBasis: { ...state.priceBasis, deemedPrice: testDeemedPriceOverride } };
  }

  return state;
};

/**
 * Converts the playground's UI state into the shared Pricing Engine API request contract — the
 * backend (QualityAdjustmentManager) performs the actual calculation, this only reshapes the
 * payload. Kept separate from the hook so it can be unit tested without React. This can't just
 * take/return `ICalculateAdjustmentsRequest` directly: `specStates` carries UI-only fields
 * (`enabled`, `collapsed`) `qualityInputs` has no place for, and deliberately keeps both rate
 * fields (percentFactor/qualityPricePerUnit) on every row so a Percentage <-> PerUnit switch
 * doesn't lose whichever one isn't currently active — see ThresholdFormulaRow's own comment.
 * Every field read below is a plain, already-gated `number` (see qualityFormulaDefaults/
 * specFormSlice) — nothing here parses, defaults, or otherwise coerces a value, it only
 * regroups fields into the shapes `qualityInputs` expects.
 */
export const buildCalculateAdjustmentsRequest = ({
  actualValues,
  deliveredQuantity,
  specStates,
  testComponentValues,
  testDeemedPriceOverrides,
  tradePrice,
}: BuildCalculateAdjustmentsRequestInput): ICalculateAdjustmentsRequest => {
  const qualityInputs = SPEC_TYPES.reduce<ICalculateAdjustmentsRequest['qualityInputs']>((acc, spec) => {
    const state = specStates[spec.key];
    if (!state.enabled) return acc;

    const testState = resolveTestSpecState(state, testComponentValues, testDeemedPriceOverrides[spec.key]);
    return { ...acc, [spec.key]: buildQualityInputForSpec(actualValues[spec.key], testState) };
  }, {});

  return {
    qualityInputs,
    shippedQuantity: deliveredQuantity,
    tradePrice,
  };
};
